import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./button";
import { Typography } from "./typography";
import { TimeSlotButton } from "./time-slot-button";

export interface FrequencyConfig {
  type: "day" | "week" | "month";
  dayOfWeek?: string;
  dayOfMonth?: string;
  time?: string;
}

interface FrequencyDropdownProps {
  value?: FrequencyConfig;
  onChange: (config: FrequencyConfig) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

type ExpandedSection = "frequency" | "options" | "time" | null;
type FrequencyType = FrequencyConfig["type"];

const TIME_SLOTS = [
  "08:00",
  "10:00",
  "12:00",
  "14:00",
  "16:00",
  "18:00",
  "20:00",
  "22:00",
  "00:00",
] as const;

export const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
] as const;

export const MONTH_OPTIONS = [
  { value: "1st", label: "1st of the month" },
  { value: "15th", label: "15th of the month" },
  { value: "last", label: "End of the month" },
] as const;

const FREQUENCY_TYPES = [
  { type: "day" as const, label: "Day" },
  { type: "week" as const, label: "Week" },
  { type: "month" as const, label: "Month" },
] as const;

const DEFAULT_CONFIG = {
  type: "week" as FrequencyType,
  dayOfWeek: "tuesday",
  dayOfMonth: "1st",
  time: "08:00",
} as const;

const DROPDOWN_STYLES = {
  maxHeight: 400,
  minHeight: 200,
  spacing: 20,
  buttonHeight: 80,
} as const;

const getDefaultConfig = (type: FrequencyType): Partial<FrequencyConfig> => {
  switch (type) {
    case "day":
      return { type, time: DEFAULT_CONFIG.time };
    case "week":
      return { type, dayOfWeek: DEFAULT_CONFIG.dayOfWeek };
    case "month":
      return { type, dayOfMonth: DEFAULT_CONFIG.dayOfMonth };
    default:
      return { type: DEFAULT_CONFIG.type, dayOfWeek: DEFAULT_CONFIG.dayOfWeek };
  }
};

export const formatDisplayValue = (
  config?: FrequencyConfig,
  placeholder = "Select frequency"
): string => {
  if (!config?.type) return placeholder;

  const { type, dayOfWeek, dayOfMonth, time } = config;

  switch (type) {
    case "day":
      return `Every Day${time ? `, ${time}` : ""}`;
    case "week": {
      const dayLabel =
        DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.label || "";
      return `Every Week${dayLabel ? `, ${dayLabel}` : ""}`;
    }
    case "month": {
      const monthLabel =
        MONTH_OPTIONS.find((m) => m.value === dayOfMonth)?.label || "";
      return `Every Month${monthLabel ? `, ${monthLabel}` : ""}`;
    }
    default:
      return placeholder;
  }
};

const calculateDropdownPosition = (triggerRect: DOMRect) => ({
  top: triggerRect.bottom + 4,
  left: triggerRect.left,
  right: window.innerWidth - triggerRect.right,
});

const calculateMaxHeight = (top: number) =>
  Math.min(
    DROPDOWN_STYLES.maxHeight,
    window.innerHeight - top - DROPDOWN_STYLES.spacing
  );

interface SectionHeaderProps {
  title: string;
  subtitle: string;
  isExpanded: boolean;
  onClick: () => void;
  className?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = React.memo(
  ({ title, subtitle, isExpanded, onClick, className = "" }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${className}`}
      aria-expanded={isExpanded}
      aria-controls={`section-${title.toLowerCase()}`}
    >
      <div className="flex flex-col items-start">
        <Typography variant="label" className="font-medium text-gray-900">
          {title}
        </Typography>
        <Typography variant="caption" className="text-gray-500">
          {subtitle}
        </Typography>
      </div>
      {isExpanded ? (
        <ChevronUp className="h-5 w-5 text-gray-400" aria-hidden="true" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
      )}
    </button>
  )
);

SectionHeader.displayName = "SectionHeader";

interface RadioOptionProps {
  value: string;
  label: string;
  isSelected: boolean;
  onChange: (value: string) => void;
  name: string;
}

const RadioOption: React.FC<RadioOptionProps> = React.memo(
  ({ value, label, isSelected, onChange, name }) => (
    <label className="flex items-center space-x-3 cursor-pointer group">
      <div className="relative">
        <input
          type="radio"
          name={name}
          value={value}
          checked={isSelected}
          onChange={() => onChange(value)}
          className="sr-only"
          aria-describedby={`${name}-${value}-label`}
        />
        <div
          className={`
          w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
          ${
            isSelected
              ? "border-pink-500 bg-pink-500"
              : "border-gray-300 group-hover:border-gray-400"
          }
        `}
        >
          {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
        </div>
      </div>
      <Typography
        variant="p"
        className="text-gray-700 group-hover:text-gray-900 transition-colors"
        id={`${name}-${value}-label`}
      >
        {label}
      </Typography>
    </label>
  )
);

RadioOption.displayName = "RadioOption";

export const CustomFrequencyDropdown: React.FC<FrequencyDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select frequency",
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
  });

  const initialConfig = useMemo(
    () => ({
      type: value?.type || DEFAULT_CONFIG.type,
      dayOfWeek:
        value?.dayOfWeek ||
        (value?.type === "week" || !value?.type
          ? DEFAULT_CONFIG.dayOfWeek
          : undefined),
      dayOfMonth:
        value?.dayOfMonth ||
        (value?.type === "month" ? DEFAULT_CONFIG.dayOfMonth : undefined),
      time:
        value?.type === "day" ? value?.time || DEFAULT_CONFIG.time : undefined,
    }),
    [value]
  );

  const [tempConfig, setTempConfig] =
    useState<Partial<FrequencyConfig>>(initialConfig);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const displayValue = useMemo(
    () => formatDisplayValue(value, placeholder),
    [value, placeholder]
  );

  const currentFrequencyDisplay = useMemo(() => {
    switch (tempConfig.type) {
      case "day":
        return "Day";
      case "week":
        return "Week";
      case "month":
        return "Month";
      default:
        return "Week";
    }
  }, [tempConfig.type]);

  const currentOptionsDisplay = useMemo(() => {
    if (tempConfig.type === "week") {
      return (
        DAYS_OF_WEEK.find((d) => d.value === tempConfig.dayOfWeek)?.label ||
        "Tuesday"
      );
    }
    if (tempConfig.type === "month") {
      return (
        MONTH_OPTIONS.find((m) => m.value === tempConfig.dayOfMonth)?.label ||
        "1st of the month"
      );
    }
    return "";
  }, [tempConfig.type, tempConfig.dayOfWeek, tempConfig.dayOfMonth]);

  const availableOptions = useMemo(() => {
    return tempConfig.type === "week" ? DAYS_OF_WEEK : MONTH_OPTIONS;
  }, [tempConfig.type]);

  const selectedOptionValue = useMemo(() => {
    return tempConfig.type === "week"
      ? tempConfig.dayOfWeek
      : tempConfig.dayOfMonth;
  }, [tempConfig.type, tempConfig.dayOfWeek, tempConfig.dayOfMonth]);

  const updatePosition = useCallback(() => {
    if (dropdownRef.current && isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect));
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        setExpandedSection(null);
      }
    },
    [isOpen]
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setExpandedSection(null);
  }, []);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      const newIsOpen = !prev;
      if (newIsOpen) {
        setExpandedSection("frequency");
      } else {
        setExpandedSection(null);
      }
      return newIsOpen;
    });
  }, [disabled]);

  const toggleSection = useCallback((section: ExpandedSection) => {
    setExpandedSection((current) => (current === section ? null : section));
  }, []);

  const handleFrequencySelect = useCallback((value: string) => {
    const type = value as FrequencyType;
    setTempConfig(getDefaultConfig(type));
    setExpandedSection(null);
  }, []);

  const handleOptionSelect = useCallback((option: string) => {
    setTempConfig((prev) => {
      if (prev.type === "week") {
        return { ...prev, dayOfWeek: option };
      }
      if (prev.type === "month") {
        return { ...prev, dayOfMonth: option };
      }
      return prev;
    });
    setExpandedSection(null);
  }, []);

  const handleTimeSelect = useCallback((time: string) => {
    setTempConfig((prev) => ({ ...prev, time }));
    setExpandedSection(null);
  }, []);

  const handleApply = useCallback(() => {
    if (tempConfig.type) {
      onChange(tempConfig as FrequencyConfig);
      closeDropdown();
    }
  }, [tempConfig, onChange, closeDropdown]);

  const handleReset = useCallback(() => {
    setTempConfig(getDefaultConfig(DEFAULT_CONFIG.type));
    setExpandedSection(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, updatePosition, handleKeyDown]);

  useEffect(() => {
    if (!isOpen) {
      setTempConfig(initialConfig);
    }
  }, [initialConfig, isOpen]);

  const renderFrequencySection = useCallback(
    () => (
      <div
        className="border border-gray-100 rounded-lg m-4"
        role="group"
        aria-labelledby="frequency-header"
      >
        <SectionHeader
          title="Every"
          subtitle={currentFrequencyDisplay}
          isExpanded={expandedSection === "frequency"}
          onClick={() => toggleSection("frequency")}
        />

        {expandedSection === "frequency" && (
          <div className="px-4 pb-4 space-y-3" id="section-frequency">
            {FREQUENCY_TYPES.map(({ type, label }) => (
              <RadioOption
                key={type}
                value={type}
                label={label}
                isSelected={tempConfig.type === type}
                onChange={handleFrequencySelect}
                name="frequency"
              />
            ))}
          </div>
        )}
      </div>
    ),
    [
      expandedSection,
      currentFrequencyDisplay,
      tempConfig.type,
      toggleSection,
      handleFrequencySelect,
    ]
  );

  const renderOptionsSection = useCallback(() => {
    if (tempConfig.type === "day") return null;

    return (
      <div
        className="border border-gray-100 rounded-lg m-4"
        role="group"
        aria-labelledby="options-header"
      >
        <SectionHeader
          title={tempConfig.type === "week" ? "On" : "On the"}
          subtitle={currentOptionsDisplay}
          isExpanded={expandedSection === "options"}
          onClick={() => toggleSection("options")}
        />

        {expandedSection === "options" && (
          <div className="px-4 pb-4 space-y-3" id="section-options">
            {availableOptions.map(({ value, label }) => (
              <RadioOption
                key={value}
                value={value}
                label={label}
                isSelected={selectedOptionValue === value}
                onChange={handleOptionSelect}
                name="options"
              />
            ))}
          </div>
        )}
      </div>
    );
  }, [
    tempConfig.type,
    expandedSection,
    currentOptionsDisplay,
    availableOptions,
    selectedOptionValue,
    toggleSection,
    handleOptionSelect,
  ]);

  const renderTimeSection = useCallback(() => {
    if (tempConfig.type !== "day") return null;

    return (
      <div
        className="border border-gray-100 rounded-lg m-4"
        role="group"
        aria-labelledby="time-header"
      >
        <SectionHeader
          title="Time"
          subtitle={tempConfig.time || DEFAULT_CONFIG.time}
          isExpanded={expandedSection === "time"}
          onClick={() => toggleSection("time")}
        />

        {expandedSection === "time" && (
          <div className="px-4 pb-4" id="section-time">
            <div
              className="grid grid-cols-3 gap-2"
              role="radiogroup"
              aria-label="Select time"
            >
              {TIME_SLOTS.map((time) => (
                <TimeSlotButton
                  key={time}
                  time={time}
                  isSelected={tempConfig.time === time}
                  onClick={handleTimeSelect}
                  variant="dropdown"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    tempConfig.type,
    tempConfig.time,
    expandedSection,
    toggleSection,
    handleTimeSelect,
  ]);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          w-full h-14 px-4 py-2 bg-white border border-gray-300 rounded-lg 
          flex items-center justify-between transition-all duration-200
          ${
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select frequency"
      >
        <span
          className={`text-left ${
            !value?.type ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {displayValue}
        </span>
        <div className="ml-2 flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
          )}
        </div>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeDropdown}
          aria-hidden="true"
        >
          <div
            className="absolute bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[280px] flex flex-col"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              right: dropdownPosition.right,
              maxHeight: calculateMaxHeight(dropdownPosition.top),
              minHeight: DROPDOWN_STYLES.minHeight,
            }}
            onClick={(e) => e.stopPropagation()}
            role="listbox"
            aria-label="Frequency options"
          >
            <div
              className="overflow-y-auto"
              style={{
                flex: "1 1 auto",
                minHeight: "120px",
                maxHeight: `calc(100% - ${DROPDOWN_STYLES.buttonHeight}px)`,
              }}
            >
              {renderFrequencySection()}
              {renderOptionsSection()}
              {renderTimeSection()}
            </div>

            <div className="flex justify-between gap-4 p-4 border-t border-gray-100 bg-white flex-shrink-0 rounded-b-lg">
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
                className="border border-gray-200 rounded-full px-4 py-2 hover:text-gray-700 transition-colors w-full"
              >
                Reset all
              </Button>
              <Button
                type="button"
                onClick={handleApply}
                className="btn-primary !w-full px-6 py-2 !text-sm"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
